/**
 * Firebase Auth setup for CortexV2 — Google (ad.net) SSO, UI-gating only.
 *
 * These config values are public by design (Firebase's web apiKey is NOT a secret; access is
 * controlled by Auth rules + authorized domains), so they're committed as defaults and can be
 * overridden per-env via VITE_FIREBASE_* if ever needed.
 *
 * NOTE: the backend (socialiq-sql / socialiq-bqv) now VERIFIES this Firebase ID token, so a
 * logged-in session IS the backend auth boundary — not just UI gating. currentIdToken() returns
 * the token the API client sends as an `Authorization: Bearer` credential.
 */
import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
  signOut,
  connectAuthEmulator,
  type User,
} from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";

const env = import.meta.env;

const firebaseConfig = {
  apiKey:            (env.VITE_FIREBASE_API_KEY as string)     ?? "AIzaSyA62Ru-qJtdCZYl0aF5ChsGCDpVuVwk8B4",
  authDomain:        (env.VITE_FIREBASE_AUTH_DOMAIN as string) ?? "socialiq-dev1.firebaseapp.com",
  projectId:         (env.VITE_FIREBASE_PROJECT_ID as string)  ?? "socialiq-dev1",
  storageBucket:     (env.VITE_FIREBASE_STORAGE_BUCKET as string) ?? "socialiq-dev1.firebasestorage.app",
  messagingSenderId: (env.VITE_FIREBASE_SENDER_ID as string)   ?? "796259140042",
  appId:             (env.VITE_FIREBASE_APP_ID as string)      ?? "1:796259140042:web:2dff57ace23263f7b70f33",
};

/** Workspace domains allowed into Cortex (UI-gating; enforced client-side). Keep in sync with
 *  firestore.rules. */
export const ALLOWED_DOMAINS = ["ad.net", "socialiq.com", "joylink.io"];

/** Human-readable list for messages/UI, e.g. "@ad.net, @socialiq.com, or @joylink.io". */
export const ALLOWED_DOMAINS_LABEL = ALLOWED_DOMAINS.map(d => `@${d}`).join(" or ");

export const DOMAIN_RESTRICTION_MESSAGE = `Access is limited to ${ALLOWED_DOMAINS_LABEL} accounts.`;

export function isAllowedUser(user: Pick<User, "email"> | null): boolean {
  const email = user?.email?.toLowerCase();
  return !!email && ALLOWED_DOMAINS.some(d => email.endsWith(`@${d}`));
}

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);

// ── Local dev only: Firebase Auth emulator ────────────────────────────────────────────────────
// When VITE_FIREBASE_AUTH_EMULATOR_HOST is set (ONLY ever in a local .env.local — see LOCAL_DEV.md),
// point Auth at the local emulator. Then "Sign in with Google" opens the emulator's account picker
// where you can sign in as ANY test user (e.g. dev@ad.net) without a real Google account, and the
// emulator issues a real ID token the local backend verifies (set FIREBASE_AUTH_EMULATOR_HOST on
// the backend too). NEVER set this var in a deployed build — production talks to real Firebase.
// Must run immediately after getAuth(), before any other auth call.
const _authEmulatorHost = env.VITE_FIREBASE_AUTH_EMULATOR_HOST as string | undefined;
if (_authEmulatorHost) {
  connectAuthEmulator(auth, _authEmulatorHost, { disableWarnings: true });
}

/** True only when pointed at the local Auth emulator (never set in a deployed build). */
export const AUTH_EMULATOR_ACTIVE = !!_authEmulatorHost;

/**
 * LOCAL DEV ONLY: sign in automatically against the Auth emulator so there is no popup to click and
 * no throwaway account to create — the local app just loads straight in. Uses a fake but VERIFIED
 * Google credential (email_verified:true, which the backend requires); the emulator accepts a JSON
 * id_token and issues a real token the local backend verifies. GUARDED by the emulator host being
 * set (never set in a deployed build), so production can never auto-login. No-op if already signed
 * in. Override the identity with VITE_DEV_LOGIN_EMAIL.
 */
let _autoLoginStarted = false;
export async function autoLoginLocalDev(): Promise<void> {
  if (!AUTH_EMULATOR_ACTIVE || auth.currentUser || _autoLoginStarted) return;
  _autoLoginStarted = true;
  const email = (env.VITE_DEV_LOGIN_EMAIL as string) || "benson.hon@ad.net";
  const claims = JSON.stringify({ sub: `local-dev-${email}`, email, email_verified: true, name: "Local Dev" });
  await signInWithCredential(auth, GoogleAuthProvider.credential(claims));
}

// Kick it off at MODULE LOAD (once) rather than inside a React effect that StrictMode double-invokes
// — the AuthProvider's onAuthStateChanged listener then simply picks up the resulting user.
if (AUTH_EMULATOR_ACTIVE) {
  autoLoginLocalDev().catch(e => console.error("[autoLoginLocalDev] failed:", e?.code, e?.message));
}

export const db = getFirestore(app);

// ── Local dev only: Firestore emulator ────────────────────────────────────────────────────────
// The role directory lives in Firestore (`users/{uid}` — see roles.ts), so the Admin screen needs it.
// Without this the local app reaches for the REAL project's Firestore and the screen can only show
// "Couldn't load users". Pointed at the emulator, logging in writes your own users/{email} document
// (roles.ts does that on every sign-in), so Admin populates itself with no seeding step.
//
// Set VITE_FIRESTORE_EMULATOR_HOST as `host:port` — startup-local.sh exports it alongside the auth
// one. NEVER set it in a deployed build. Must run before any Firestore read/write.
const _firestoreEmulator = env.VITE_FIRESTORE_EMULATOR_HOST as string | undefined;
if (_firestoreEmulator) {
  const [fsHost, fsPort] = _firestoreEmulator.replace(/^https?:\/\//, "").split(":");
  connectFirestoreEmulator(db, fsHost, Number(fsPort) || 8088);
}

const provider = new GoogleAuthProvider();
// No `hd` hint: it only accepts a single domain and would filter out the others. The account
// chooser shows all accounts; enforcement is the isAllowedUser() check after sign-in.
provider.setCustomParameters({ prompt: "select_account" });

/** Sign in with Google; rejects (and signs out) if the account isn't an allowed-domain address. */
export async function signInWithGoogle(): Promise<User> {
  const result = await signInWithPopup(auth, provider);
  if (!isAllowedUser(result.user)) {
    await signOut(auth);
    throw new Error(DOMAIN_RESTRICTION_MESSAGE);
  }
  return result.user;
}

export function signOutUser(): Promise<void> {
  return signOut(auth);
}

/** Current signed-in email. Null before login. (The backend now derives the audit actor from the
 *  verified token itself, so this is for UI display, not a trusted header.) */
export function currentActorEmail(): string | null {
  return auth.currentUser?.email ?? null;
}

/** Current Firebase ID token, for the API client's `Authorization: Bearer` header. Firebase
 *  auto-refreshes it when near expiry; pass force=true to bypass the cache (used to retry once
 *  after a backend 401). Null when signed out. */
export async function currentIdToken(force = false): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(force);
}
